import { useAuth } from './useAuth';

/**
 * How far a grant has to reach for the caller's purpose.
 *
 * - `'context'` (the default) — held anywhere in the active context. The right
 *   question for a division-scoped resource: a division administrator's grant
 *   covers their own devices, regions and credentials.
 * - `'organization'` — held across the whole organization. The right question for
 *   an organization-level resource (its notification routes, its settings), which
 *   a division-scoped grant does not reach.
 */
export type PermissionScope = 'context' | 'organization';

/**
 * Whether the current user holds a permission in their active context.
 *
 * The same list `PermissionOutlet` gates routes on, asked about one control
 * instead of a whole page. Not to be confused with `usePermissions()`, which
 * fetches the global permission *catalog* for the admin screens that render it —
 * that one says which permissions exist, this one says which the user has.
 *
 * Use it to disable an action with an explanation rather than offering it and
 * letting the request come back 403: a control that fails when pressed is
 * indistinguishable from a broken one.
 *
 * **Pass `'organization'` when the endpoint behind the control authorizes against
 * the organization.** A permission name says nothing about reach: an
 * Administrator inside a division context holds `notification_route:read` for
 * that division, spelled exactly like the grant that reads the organization's
 * routes. Gating on the name alone offered an app-wide banner to every division
 * administrator, whose fetch was then refused with `wrong_context` — surfacing as
 * a "switch context?" dialog on whatever page they were on.
 */
export function usePermission(name: string, scope: PermissionScope = 'context'): boolean {
  const { user } = useAuth();
  const held = scope === 'organization' ? user?.organizationPermissions : user?.permissions;
  return held?.includes(name) ?? false;
}
